/**
 * `/catalog` — the learner course catalog (SCH Milestone A).
 *
 * Reads `learning.courses.catalog` (PUBLISHED courses only, with the caller's
 * own `enrolledAlready` bit + `enrollmentStatus`) and renders a responsive card
 * grid. The card action reflects the caller's own enrollment state:
 *   - 'active'   → primary "Open" → `/course/$courseId`.
 *   - 'pending'  → disabled "Pending approval" + a muted "waiting for teacher" note.
 *   - 'rejected' → disabled "Application declined" + a muted "wasn't approved" note.
 *   - null       → an enroll button → `learning.enrollments.selfEnrollFree`. For
 *     approval-gated courses (`requiresEnrollmentApproval`) it reads
 *     "Request to join" / "Requesting…"; otherwise "Enroll free" / "Enrolling…".
 *     Success invalidates the catalog query so the card flips to its new state.
 *   - a course with a price (#50) → its price and "Buy" → `<BuyCourseDialog>`.
 *     Payment is simulated until a centre asks for real online payment.
 *
 * Kind vocabulary is inlined (mirrors `COURSE_KINDS` in the learning router /
 * `@mangrove/core/learning`) — this SPA must never import `@mangrove/core`.
 */
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, CheckCircle2, ChevronDown, GraduationCap, Search } from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import { Badge, Button, Card, CardContent, cn, EmptyState, Input, Skeleton } from '@mangrove/ui';
import { trpc, type AppRouter } from '../../lib/trpc.js';
import { RequireAuth } from '../../lib/auth.js';
import { LearnShell } from '../../components/shell.js';
import { BuyCourseDialog, money } from '../../components/buy-course-dialog.js';

export const Route = createFileRoute('/catalog/')({
  component: CatalogPage,
});

type CatalogCourse =
  inferRouterOutputs<AppRouter>['learning']['courses']['catalog']['courses'][number];

/** Mirrors COURSE_KINDS — 'academic' | 'training' | 'onboarding' | 'compliance'. */
const KIND_LABEL: Record<string, string> = {
  academic: 'Academic',
  training: 'Training',
  onboarding: 'Onboarding',
  compliance: 'Compliance',
};

function kindLabelOf(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

/**
 * Cover tint per kind — academic/training read as primary/info-toned, compliance
 * as warning-toned, everything else falls back to a neutral primary tint. Tokens
 * only (no raw color); mirrors the prototype's kind-tinted cover glyph.
 */
function coverToneOf(kind: string): { cover: string; glyph: string; badge: string } {
  switch (kind) {
    case 'compliance':
      return {
        cover: 'bg-warning/10',
        glyph: 'bg-warning/15 text-warning',
        badge: 'border-warning/30 bg-warning/10 text-warning',
      };
    case 'training':
    case 'onboarding':
      return {
        cover: 'bg-brand-blue/10',
        glyph: 'bg-brand-blue/15 text-brand-blue',
        badge: 'border-brand-blue/30 bg-brand-blue/10 text-brand-blue',
      };
    default:
      return {
        cover: 'bg-primary/10',
        glyph: 'bg-primary/15 text-primary',
        badge: 'border-primary/30 bg-primary/10 text-primary',
      };
  }
}

/** Client-side sort options — pure over the loaded catalog (no server round-trip). */
type SortKey = 'newest' | 'title-asc' | 'title-desc';
const SORT_OPTIONS: ReadonlyArray<{ value: SortKey; label: string }> = [
  // 'newest' is the identity order — the catalog query already returns
  // newest-first (orderBy desc(createdAt)), so we leave the filtered list as-is.
  { value: 'newest', label: 'Newest' },
  { value: 'title-asc', label: 'Title A–Z' },
  { value: 'title-desc', label: 'Title Z–A' },
];

function CatalogPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Courses" wide>
        <CatalogContent />
      </LearnShell>
    </RequireAuth>
  );
}

function CatalogContent(): React.JSX.Element {
  const catalog = trpc.learning.courses.catalog.useQuery();
  const allCourses = catalog.data?.courses ?? [];

  // Client-side search + kind filter (Ishraf v1 QA: the catalog must be
  // searchable + filterable). Pure over the loaded list — the catalog is a
  // small published set, so no server round-trip is needed.
  const [search, setSearch] = React.useState('');
  const [kind, setKind] = React.useState<string>('all');
  const [sort, setSort] = React.useState<SortKey>('newest');
  // SCH-I3-C4: period filter (from the course's startsOn/endsOn) + "enrolled
  // since/until" filter (the caller's own enrolledAt). Client-side like the rest.
  const [period, setPeriod] = React.useState<'all' | 'ongoing' | 'upcoming' | 'ended'>('all');
  const [enrolledFrom, setEnrolledFrom] = React.useState('');
  const [enrolledTo, setEnrolledTo] = React.useState('');

  // Only offer kind chips for kinds that actually appear in the catalog.
  const kinds = React.useMemo(
    () => ['all', ...[...new Set(allCourses.map((c) => c.kind))].sort()],
    [allCourses],
  );

  // Per-kind counts for the filter chips (prototype shows a count badge on each chip).
  const kindCounts = React.useMemo(() => {
    const counts: Record<string, number> = { all: allCourses.length };
    for (const c of allCourses) counts[c.kind] = (counts[c.kind] ?? 0) + 1;
    return counts;
  }, [allCourses]);

  // "Enrolled" pill in the page head — count of courses the caller is active in.
  const enrolledCount = React.useMemo(
    () => allCourses.filter((c) => c.enrollmentStatus === 'active').length,
    [allCourses],
  );

  const courses = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const today = new Date().toISOString().slice(0, 10);
    const filtered = allCourses.filter((c) => {
      if (kind !== 'all' && c.kind !== kind) return false;
      // SCH-I3-C4 period filter: no-dates courses only match 'all'/'ongoing'
      // (a course without a period is treated as always-running).
      if (period !== 'all') {
        const started = c.startsOn == null || c.startsOn <= today;
        const ended = c.endsOn != null && c.endsOn < today;
        const bucket = !started ? 'upcoming' : ended ? 'ended' : 'ongoing';
        if (bucket !== period) return false;
      }
      // SCH-I3-C4 enrollment-date filter — only meaningful on enrolled courses.
      if (enrolledFrom || enrolledTo) {
        if (!c.enrolledAt) return false;
        const at = new Date(c.enrolledAt).toISOString().slice(0, 10);
        if (enrolledFrom && at < enrolledFrom) return false;
        if (enrolledTo && at > enrolledTo) return false;
      }
      if (!q) return true;
      return c.title.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q);
    });
    // SCH-I3-C3: courses you're actively enrolled in ALWAYS sort on top;
    // within each group the chosen sort applies ('newest' = as-returned order).
    const rank = (c: CatalogCourse): number => (c.enrollmentStatus === 'active' ? 0 : 1);
    return [...filtered].sort((a, b) => {
      const byEnrolled = rank(a) - rank(b);
      if (byEnrolled !== 0) return byEnrolled;
      if (sort === 'newest') return 0; // stable sort keeps the backend order
      const cmp = a.title.localeCompare(b.title);
      return sort === 'title-desc' ? -cmp : cmp;
    });
  }, [allCourses, search, kind, sort, period, enrolledFrom, enrolledTo]);

  return (
    <div className="space-y-6">
      <div className="animate-fade-in flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Catalog</p>
          <h2 className="text-xl font-semibold tracking-tight">Browse courses</h2>
          <p className="max-w-[46ch] text-sm text-muted-foreground">
            Explore practice tests, lesson tracks, and question banks. Every published course is
            free to join.
          </p>
        </div>
        {!catalog.isPending && !catalog.error && allCourses.length > 0 ? (
          <Badge
            variant="outline"
            className="gap-1.5 border-primary/30 bg-primary/10 px-3 py-1.5 text-primary"
          >
            <CheckCircle2 className="size-4" aria-hidden="true" />
            <span className="tabular-nums">{enrolledCount}</span> enrolled
          </Badge>
        ) : null}
      </div>

      {catalog.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <Card key={i} className="card-soft flex flex-col overflow-hidden">
              <Skeleton className="aspect-[16/9] w-full rounded-none" />
              <CardContent className="space-y-3 p-4">
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-11 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : catalog.error ? (
        <EmptyState
          icon={GraduationCap}
          title="Couldn’t load the catalog"
          description={catalog.error.message}
          action={
            <Button variant="outline" onClick={() => void catalog.refetch()}>
              Try again
            </Button>
          }
        />
      ) : allCourses.length === 0 ? (
        <EmptyState
          icon={GraduationCap}
          title="No courses published yet"
          description="New courses will show up here as soon as they’re published — check back soon."
        />
      ) : (
        <>
          <Card className="card-soft animate-fade-in">
            <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search courses, topics, exams…"
                  aria-label="Search courses"
                  data-testid="catalog-search"
                  className="pl-9"
                />
              </div>
              <div className="relative sm:w-48">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  aria-label="Sort courses"
                  data-testid="catalog-sort"
                  className="press min-h-touch w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
              </div>
              {/* SCH-I3-C4: course-period filter. */}
              <div className="relative sm:w-44">
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as typeof period)}
                  aria-label="Filter by course period"
                  data-testid="catalog-period"
                  className="press min-h-touch w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="all">Any period</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="ended">Ended</option>
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
              </div>
              {/* SCH-I3-C4: enrolled-between filter (your own enrollment date). */}
              <div className="flex items-center gap-1.5" data-testid="catalog-enrolled-range">
                <Input
                  type="date"
                  value={enrolledFrom}
                  onChange={(e) => setEnrolledFrom(e.target.value)}
                  aria-label="Enrolled from"
                  className="sm:w-36"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={enrolledTo}
                  onChange={(e) => setEnrolledTo(e.target.value)}
                  aria-label="Enrolled until"
                  className="sm:w-36"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center justify-between gap-3">
            {kinds.length > 2 ? (
              <div
                className="flex flex-wrap items-center gap-2"
                role="group"
                aria-label="Filter by kind"
              >
                {kinds.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKind(k)}
                    data-testid={`catalog-kind-${k}`}
                    aria-pressed={kind === k}
                    className={cn(
                      'press inline-flex min-h-touch items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background',
                      kind === k
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-muted-foreground hover:border-primary hover:text-primary',
                    )}
                  >
                    {k === 'all' ? 'All' : kindLabelOf(k)}
                    <span className="text-xs font-bold tabular-nums opacity-70">
                      {kindCounts[k] ?? 0}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <span />
            )}
            <span
              className="text-sm text-muted-foreground"
              data-testid="catalog-count"
              aria-live="polite"
            >
              <b className="font-bold tabular-nums text-foreground">{courses.length}</b>{' '}
              {courses.length === 1 ? 'course' : 'courses'}
            </span>
          </div>

          {courses.length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="No courses match your search"
              description="Try a different search or clear the filters."
            />
          ) : (
            <div className="ls-stagger grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CourseCard({ course }: { course: CatalogCourse }): React.JSX.Element {
  const utils = trpc.useUtils();
  const enroll = trpc.learning.enrollments.selfEnrollFree.useMutation({
    onSuccess: () => void utils.learning.courses.catalog.invalidate(),
  });
  // #50: a priced course is bought (payment simulated for now), not joined free.
  const [buying, setBuying] = React.useState(false);

  const tone = coverToneOf(course.kind);

  return (
    <Card
      data-testid="course-card"
      className="card-soft card-lift press flex flex-col overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className={cn('relative grid aspect-[16/9] place-items-center', tone.cover)}>
        <Badge
          variant="outline"
          className={cn('absolute left-3 top-3 backdrop-blur-sm', tone.badge)}
        >
          {kindLabelOf(course.kind)}
        </Badge>
        {course.enrollmentStatus === 'active' ? (
          <Badge
            variant="outline"
            className="absolute right-3 top-3 gap-1 border-primary/30 bg-card/80 text-primary backdrop-blur-sm"
          >
            <CheckCircle2 className="size-3.5" aria-hidden="true" />
            Enrolled
          </Badge>
        ) : null}
        <span
          className={cn(
            'flex size-14 items-center justify-center rounded-xl bg-card/70 shadow-sm',
            tone.glyph,
          )}
        >
          <GraduationCap className="size-6" aria-hidden="true" />
        </span>
      </div>
      <CardContent className="flex flex-1 flex-col gap-2 p-4">
        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          {kindLabelOf(course.kind)}
        </span>
        <h3 className="line-clamp-2 min-h-[2.75rem] text-base font-semibold leading-snug tracking-tight">
          {course.title}
        </h3>
        <p className="line-clamp-2 min-h-[2.5rem] text-sm text-muted-foreground">
          {course.description ?? 'No description yet — open the course to see what’s inside.'}
        </p>
        <div className="mt-auto space-y-2 pt-2">
          {course.price && course.enrollmentStatus !== 'active' ? (
            <p className="text-sm font-semibold" data-testid="course-price">
              {money(course.price.priceMinor, course.price.currency)}
            </p>
          ) : null}
          {enroll.error ? <p className="text-xs text-destructive">{enroll.error.message}</p> : null}
          {course.enrollmentStatus === 'active' ? (
            <Button
              asChild
              className="press group min-h-touch w-full"
              data-testid="open-course-button"
            >
              <Link to="/course/$courseId" params={{ courseId: course.id }}>
                Open
                <ArrowRight
                  className="size-4 transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </Link>
            </Button>
          ) : course.enrollmentStatus === 'pending' ? (
            <div className="space-y-1">
              <Button
                variant="outline"
                className="press min-h-touch w-full"
                disabled
                data-testid="enrollment-pending-button"
              >
                Pending approval
              </Button>
              <p className="text-xs text-muted-foreground">
                Waiting for the teacher to approve your request.
              </p>
            </div>
          ) : course.enrollmentStatus === 'rejected' ? (
            <div className="space-y-1">
              <Button
                variant="outline"
                className="press min-h-touch w-full"
                disabled
                data-testid="enrollment-rejected-button"
              >
                Application declined
              </Button>
              <p className="text-xs text-muted-foreground">
                Your enrollment request wasn’t approved.
              </p>
            </div>
          ) : course.price ? (
            <>
              <Button
                className="press min-h-touch w-full"
                onClick={() => setBuying(true)}
                data-testid="buy-button"
              >
                Buy
              </Button>
              <BuyCourseDialog
                courseId={course.id}
                courseTitle={course.title}
                defaultPlanId={course.price.planId}
                open={buying}
                onOpenChange={setBuying}
              />
            </>
          ) : (
            <Button
              variant="outline"
              className="press min-h-touch w-full"
              disabled={enroll.isPending}
              onClick={() => enroll.mutate({ courseId: course.id })}
              data-testid="enroll-button"
            >
              {course.requiresEnrollmentApproval
                ? enroll.isPending
                  ? 'Requesting…'
                  : 'Request to join'
                : enroll.isPending
                  ? 'Enrolling…'
                  : 'Enroll free'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
